import IWorkspace, {
  IApplication,
  VerbBuildStatus,
  WorkspaceStartStatus,
  WorkspaceStatus,
  HealthCheck as IHealthCheck,
} from "models/Workspace.model";
import { Environment, File, Timestamp, Task, EnvironmentBuild } from "./types";
import { getNotebookNameFromUrl } from ".";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CloudCredService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/cloud_cred_connect";
import { createConnectClient } from "server/connect";
import { LifeCycleScript } from "../shared/BuildTypes";
import { mapKeys, camelCase } from "lodash";
import { getHealthCheckProbePort } from "utils/healthChecks";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { formatByteValue } from "utils/byteValues";
import { normalizeEnvironmentBytes } from "utils/instanceTypeBytes";
const cloudCredClient = createConnectClient(CloudCredService);

type RuntimeIngressAccessPolicy = {
  access_policy?: {
    external_auth_id?: string;
    is_public?: boolean;
  };
  external_auth_id?: string;
  is_public?: boolean;
};

export const parseTimestamp = (timestamp: Timestamp | undefined) => {
  // Ensure we have a valid timestamp object
  if (!timestamp || typeof timestamp.seconds === "undefined") {
    return null;
  }

  // Convert seconds to milliseconds (JavaScript Date uses milliseconds)
  const milliseconds = Number(timestamp.seconds) * 1000;

  // Add nanoseconds (convert to milliseconds)
  // Note: JavaScript Date only has millisecond precision, so we lose some precision here
  const nanosToMillis = (timestamp.nanos || 0) / 1000000;

  // Create a new Date object
  return new Date(milliseconds + nanosToMillis);
};

const getFirstGpuFromEnvironment = (environment: Environment) =>
  environment.instance_type?.supported_gpus?.[0] ??
  environment.instance?.instance_type_info?.supported_gpus?.[0];

export const doesEnvironmentHaveGpu = (environment: Environment): boolean =>
  Boolean(getFirstGpuFromEnvironment(environment));

export const getGpuNameFromEnvironment = (
  environment: Environment,
): string | undefined => {
  return getFirstGpuFromEnvironment(environment)?.name;
};

export const getGpuManufacturerFromEnvironment = (
  environment: Environment,
): string | undefined => {
  return getFirstGpuFromEnvironment(environment)?.manufacturer;
};

export const getGpuMemoryFromEnvironment = (
  environment: Environment,
): string | undefined => {
  const topLevelGpu = environment.instance_type?.supported_gpus?.[0];
  const nestedGpu =
    environment.instance?.instance_type_info?.supported_gpus?.[0];
  return (
    formatByteValue(topLevelGpu?.memory_bytes) ??
    formatByteValue(nestedGpu?.memory_bytes) ??
    topLevelGpu?.memory ??
    nestedGpu?.memory
  );
};

export const getGpuCountFromEnvironment = (
  environment: Environment,
): number | undefined => {
  return getFirstGpuFromEnvironment(environment)?.count;
};

export const getVcpuFromEnvironment = (
  environment: Environment,
): number | undefined => {
  return (
    environment.instance_type?.vcpu ??
    environment.instance?.instance_type_info?.vcpu
  );
};

export const getMemoryFromEnvironment = (
  environment: Environment,
): string | number | undefined => {
  const topLevel = environment.instance_type;
  const nested = environment.instance?.instance_type_info;
  return (
    formatByteValue(topLevel?.memory_bytes) ??
    formatByteValue(nested?.memory_bytes) ??
    topLevel?.memory ??
    nested?.memory
  );
};

export const getEnvironmentCloudCredID = (
  environment: Environment | null | undefined,
): string | undefined =>
  environment?.instance?.cloud_cred_id ||
  environment?.provision?.createInstanceRequest?.cloud_cred_id;

export const getWorkspacePlatformTypeFromEnvironment = (
  environment: Environment,
): string => {
  const cloudCredId = getEnvironmentCloudCredID(environment);

  const { data: providerId, isLoading } = useQuery(
    ["cloudCredProvider", cloudCredId],
    async () => {
      if (cloudCredId) {
        const res = await cloudCredClient.getCloudCred({ cloudCredId });
        return res.cloudCred?.providerId;
      }
      return undefined;
    },
    {
      enabled: !!cloudCredId,
      staleTime: 5 * 60 * 1000,
    },
  );

  return useMemo(() => {
    if (isLoading) return "loading...";
    return providerId || "Unknown";
  }, [providerId, isLoading]);
};

type EnvironmentStatusLike = EnvironmentStatus | string | null | undefined;

export const normalizeEnvironmentStatus = (
  status: EnvironmentStatusLike,
): EnvironmentStatus | null => {
  if (typeof status === "number") {
    return status === EnvironmentStatus.UNSPECIFIED ? null : status;
  }
  if (!status) return null;

  const normalized = status
    .trim()
    .toUpperCase()
    .replace(/^ENVIRONMENT_STATUS_/, "")
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
  const enumValue =
    EnvironmentStatus[normalized as keyof typeof EnvironmentStatus];
  return typeof enumValue === "number" &&
    enumValue !== EnvironmentStatus.UNSPECIFIED
    ? enumValue
    : null;
};

export const hasKnownEnvironmentStatus = (
  environment: Pick<Environment, "status"> | null | undefined,
): boolean => normalizeEnvironmentStatus(environment?.status) !== null;

export const shouldShowInstanceDetailsDespiteFailure = (
  environment: Pick<Environment, "status"> | null | undefined,
): boolean => {
  switch (normalizeEnvironmentStatus(environment?.status)) {
    case EnvironmentStatus.STARTING_FAILED:
    case EnvironmentStatus.BUILDING_FAILED:
    case EnvironmentStatus.STOPPING_FAILED:
    case EnvironmentStatus.TERMINATING_FAILED:
      return true;
    default:
      return false;
  }
};

export const isEnvironmentPastBuild = (
  environment: Pick<Environment, "status">,
): boolean => {
  switch (normalizeEnvironmentStatus(environment.status)) {
    case EnvironmentStatus.PENDING:
    case EnvironmentStatus.PREPARING:
    case EnvironmentStatus.PREPARING_FAILED:
    case EnvironmentStatus.PROVISIONING:
    case EnvironmentStatus.PROVISIONING_FAILED:
    case EnvironmentStatus.BUILDING:
    case EnvironmentStatus.BUILDING_FAILED:
    case null:
      return false;
    default:
      return true;
  }
};

export const canRequestEnvironmentStart = (
  status: EnvironmentStatusLike,
): boolean => {
  switch (normalizeEnvironmentStatus(status)) {
    case EnvironmentStatus.STOPPED:
    case EnvironmentStatus.STARTING_FAILED:
      return true;
    default:
      return false;
  }
};

export const canRequestEnvironmentStop = (
  status: EnvironmentStatusLike,
): boolean => {
  switch (normalizeEnvironmentStatus(status)) {
    case EnvironmentStatus.RUNNING:
    case EnvironmentStatus.STOPPING_FAILED:
    case EnvironmentStatus.STARTING_FAILED:
    case EnvironmentStatus.BUILDING_FAILED:
      return true;
    default:
      return false;
  }
};

export const isProviderVMRunning = (
  environment: Pick<Environment, "instance"> | null | undefined,
): boolean => environment?.instance?.status?.lifecycle_status === "running";

export const isRunningEnvironmentStatus = (
  status: EnvironmentStatusLike,
): boolean => normalizeEnvironmentStatus(status) === EnvironmentStatus.RUNNING;

export const isEnvironmentStartInProgress = (
  status: EnvironmentStatusLike,
): boolean => normalizeEnvironmentStatus(status) === EnvironmentStatus.STARTING;

export const isEnvironmentStopInProgress = (
  status: EnvironmentStatusLike,
): boolean => normalizeEnvironmentStatus(status) === EnvironmentStatus.STOPPING;

export const getWorkspaceStatusFromEnvironmentStatus = (
  status: EnvironmentStatusLike,
): WorkspaceStatus | null => {
  switch (normalizeEnvironmentStatus(status)) {
    case EnvironmentStatus.PENDING:
    case EnvironmentStatus.PREPARING:
    case EnvironmentStatus.PROVISIONING:
    case EnvironmentStatus.BUILDING:
      return WorkspaceStatus.Deploying;
    case EnvironmentStatus.RUNNING:
      return WorkspaceStatus.Running;
    case EnvironmentStatus.STOPPING:
      return WorkspaceStatus.Stopping;
    case EnvironmentStatus.STOPPED:
      return WorkspaceStatus.Stopped;
    case EnvironmentStatus.STARTING:
      return WorkspaceStatus.Starting;
    case EnvironmentStatus.TERMINATING:
      return WorkspaceStatus.Deleting;
    case EnvironmentStatus.TERMINATED:
      return WorkspaceStatus.Stopped;
    case EnvironmentStatus.PREPARING_FAILED:
    case EnvironmentStatus.PROVISIONING_FAILED:
    case EnvironmentStatus.BUILDING_FAILED:
    case EnvironmentStatus.INSTANCE_LOST:
    case EnvironmentStatus.STOPPING_FAILED:
    case EnvironmentStatus.STARTING_FAILED:
    case EnvironmentStatus.TERMINATING_FAILED:
      return WorkspaceStatus.Failure;
    default:
      return null;
  }
};

export interface EnvironmentBillingState {
  compute: boolean;
  storage: boolean;
}

const knownInstanceLifecycles = new Set([
  "pending",
  "running",
  "stopping",
  "stopped",
  "suspending",
  "suspended",
  "terminating",
  "terminated",
  "failed",
]);

const storageInactiveLifecycles = new Set([
  "pending",
  "terminating",
  "terminated",
]);

/**
 * Uses Dev Plane's instance usage predicates for every known lifecycle.
 * Environment.status is deliberately ignored because it represents
 * orchestration, not billing. The instance attachment is expected to be
 * present. A missing instance cannot accrue instance or storage charges.
 * Unknown lifecycle values conservatively display as charging so we never
 * incorrectly tell a user that they are not accruing charges. An instance
 * without a provider ID and lifecycle is the pre-provider-allocation case and
 * cannot have generated usage.
 */
export const getEnvironmentBillingState = (
  environment: Pick<Environment, "instance"> | null | undefined,
): EnvironmentBillingState => {
  const instance = environment?.instance;
  if (!instance) {
    return { compute: false, storage: false };
  }

  const lifecycle = instance.status?.lifecycle_status;
  if (!instance.provider_instance_id && !lifecycle) {
    return { compute: false, storage: false };
  }

  const compute =
    lifecycle === "running" || !knownInstanceLifecycles.has(lifecycle || "");
  const storage =
    instance.retire_time === undefined &&
    !storageInactiveLifecycles.has(lifecycle || "");

  return { compute, storage };
};

export const getEnvironmentStatusLabelFromStatus = (
  status: EnvironmentStatusLike,
): string | null => {
  switch (normalizeEnvironmentStatus(status)) {
    case EnvironmentStatus.PENDING:
      return "Pending";
    case EnvironmentStatus.PREPARING:
      return "Preparing";
    case EnvironmentStatus.PREPARING_FAILED:
      return "Preparing failed";
    case EnvironmentStatus.PROVISIONING:
      return "Provisioning";
    case EnvironmentStatus.PROVISIONING_FAILED:
      return "Provisioning failed";
    case EnvironmentStatus.BUILDING:
      return "Building";
    case EnvironmentStatus.BUILDING_FAILED:
      return "Build failed";
    case EnvironmentStatus.RUNNING:
      return "Running";
    case EnvironmentStatus.INSTANCE_LOST:
      return "Instance lost";
    case EnvironmentStatus.STOPPING:
      return "Stopping";
    case EnvironmentStatus.STOPPING_FAILED:
      return "Stop failed";
    case EnvironmentStatus.STOPPED:
      return "Stopped";
    case EnvironmentStatus.STARTING:
      return "Starting";
    case EnvironmentStatus.STARTING_FAILED:
      return "Start failed";
    case EnvironmentStatus.TERMINATING:
      return "Terminating";
    case EnvironmentStatus.TERMINATING_FAILED:
      return "Terminate failed";
    case EnvironmentStatus.TERMINATED:
      return "Terminated";
    default:
      return null;
  }
};

export const getEnvironmentStatus = (
  environment: Environment,
): WorkspaceStatus => {
  const backendStatus = getWorkspaceStatusFromEnvironmentStatus(
    environment.status,
  );
  if (backendStatus) {
    return backendStatus;
  }

  if (!environment.instance) {
    return WorkspaceStatus.Failure;
  }

  if (!environment.instance.status) {
    console.error("Environment status is undefined");
    return WorkspaceStatus.Deploying;
  }

  const { object_status, lifecycle_status } = environment.instance.status;

  // Check object status first
  if (object_status === "pending") {
    return WorkspaceStatus.Deploying;
  }
  if (object_status === "error") {
    return WorkspaceStatus.Failure;
  }

  // Handle lifecycle status
  switch (lifecycle_status) {
    case "pending":
      // Note: We can't exactly replicate the backend logic here since we don't have
      // access to runningAt. We might need to add that to your Environment type
      return WorkspaceStatus.Deploying;

    case "running":
      return WorkspaceStatus.Running;

    case "stopping":
      return WorkspaceStatus.Stopping;

    case "stopped":
      return WorkspaceStatus.Stopped;

    case "suspending":
      return WorkspaceStatus.Stopping;

    case "suspended":
      return WorkspaceStatus.Stopped;

    case "terminating":
      // Note: The container check logic from backend would need to be
      // replicated here if needed
      return WorkspaceStatus.Deleting;

    case "terminated":
      return WorkspaceStatus.Stopped;

    case "failed":
      return WorkspaceStatus.Failure;

    case "":
      return WorkspaceStatus.Deleting;

    default:
      console.error(`Unknown lifecycle status: ${lifecycle_status}`);
      return WorkspaceStatus.Failure;
  }
};

export const isEnvironmentDeleteBlockedByStatus = (
  environment: Environment,
): boolean => {
  const backendEnvironmentStatus = normalizeEnvironmentStatus(
    environment.status,
  );
  const environmentStatus = getEnvironmentStatus(environment);
  return backendEnvironmentStatus
    ? backendEnvironmentStatus === EnvironmentStatus.PENDING ||
      backendEnvironmentStatus === EnvironmentStatus.PREPARING ||
      backendEnvironmentStatus === EnvironmentStatus.PROVISIONING ||
      backendEnvironmentStatus === EnvironmentStatus.TERMINATING ||
      backendEnvironmentStatus === EnvironmentStatus.TERMINATED
    : environmentStatus === WorkspaceStatus.Deploying ||
      environmentStatus === WorkspaceStatus.Deleting;
};

export const getEnvironmentStatusLabel = (environment: Environment): string => {
  const workspaceStatus = getEnvironmentStatus(environment);
  const backendLabel = getEnvironmentStatusLabelFromStatus(environment.status);
  if (backendLabel) return backendLabel;

  switch (workspaceStatus) {
    case WorkspaceStatus.Deploying:
      return "Deploying";
    case WorkspaceStatus.Running:
      return "Running";
    case WorkspaceStatus.Stopping:
      return "Stopping";
    case WorkspaceStatus.Stopped:
      return "Stopped";
    case WorkspaceStatus.Starting:
      return "Starting";
    case WorkspaceStatus.Deleting:
      return "Deleting";
    case WorkspaceStatus.Failure:
      return "Failed";
    default:
      return getEnvironmentStatus(environment);
  }
};

// Name of the health check a workspace auto-creates per HTTP ingress. The
// backend writes the label as `ingress-http-{hostname}` — mirror that here so
// we can correlate health checks back to ingresses without a flattened
// `healthCheckID` on each application.
const HEALTH_CHECK_NAME_PREFIX = "ingress-http-";

const getIngressPort = (
  port: number | string | undefined,
): string | undefined => (port == null ? undefined : String(port));

// Map devplane IngressGatewayStatus enum (numeric) to the legacy
// workspace.tunnel.tunnelStatus string values. The brev-deploy
// `setIngressGatewaysOnWorkspace` helper does the same mapping server-side.
export const getTunnelStatusFromEnvironment = (
  environment: Environment,
): string => {
  if (environment.tunnel?.tunnelStatus) return environment.tunnel.tunnelStatus;

  const status = environment.ingress_gateways?.[0]?.status;
  // 2 = HEALTHY, 3 = DOWN — anything else (unknown/inactive/missing) is
  // treated as unavailable by the UI.
  if (status === 2) return "HEALTHY";
  if (status === 3) return "UNHEALTHY";
  return "UNAVAILABLE";
};

// Build an IApplication-shaped object from each devplane ingress, mirroring
// brev-deploy's `ingressToApplication` (internal/routes/environment_setup.go).
// Runtime ingress_gateways arrive after provisioning; the create response only
// has build.ingress_gateway, so use that as a fallback for early CTA rendering.
// `name` is reconstructed from the hostname because brev-deploy's
// `generateCloudflareHostname` formats it as `{name}.{domain}`, so
// `hostname.split(".")[0]` matches the stored WorkspaceApplication.Name.
export const getIngressApplicationsFromEnvironment = (
  environment: Environment,
  options: { includeBuildFallback?: boolean } = {},
): IApplication[] => {
  const includeBuildFallback = options.includeBuildFallback ?? true;
  const tunnelApplications = environment.tunnel?.applications ?? [];
  const runtimeIngresses =
    environment.ingress_gateways?.flatMap(
      (gateway) => gateway.ingresses ?? [],
    ) ?? [];
  const buildIngresses =
    environment.build?.ingress_gateway?.flatMap(
      (gateway) => gateway.ingresses ?? [],
    ) ?? [];
  const ingresses =
    runtimeIngresses.length > 0
      ? runtimeIngresses
      : includeBuildFallback
        ? buildIngresses
        : [];
  const isSkybridge = environment.labels?.["tunnel-provider"] === "skybridge";
  if (!isSkybridge && tunnelApplications.length > 0) return tunnelApplications;
  if (
    includeBuildFallback &&
    isSkybridge &&
    ingresses.length === 0 &&
    tunnelApplications.length > 0
  ) {
    return tunnelApplications;
  }

  const healthChecks = environment.health_checks ?? [];
  return ingresses.map((ingress) => {
    const hostname = ingress.hostname;
    const hcName = `${HEALTH_CHECK_NAME_PREFIX}${hostname}`;
    const ingressPort = getIngressPort(ingress.port);
    const matchingHealthCheck =
      healthChecks.find((hc) => hc.labels?.["name"] === hcName) ??
      healthChecks.find((hc) => getHealthCheckProbePort(hc) === ingressPort);
    const accessPolicies = ingress.access_policies ?? [];
    const getAccessPolicy = (policy: RuntimeIngressAccessPolicy) =>
      policy.access_policy ?? policy;
    const allowedUserAuthIDs = accessPolicies
      .map((p) => getAccessPolicy(p)?.external_auth_id)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    const allowEveryone = accessPolicies.some(
      (p) => getAccessPolicy(p)?.is_public === true,
    );
    return {
      cloudflareApplicationID: ingress.ingress_id ?? "",
      cloudflareDnsRecordID: "",
      hostname,
      name: hostname.split(".")[0],
      serviceType: ingress.service_type,
      port: ingress.port,
      userApplicationSetupBash: "",
      policy: {
        allowedUserAuthIDs,
        allowEveryone,
        apiKey: {
          enabled: false,
          id: "",
          clientID: "",
          clientSecret: "",
        },
      },
      healthCheckID: matchingHealthCheck?.health_check_id ?? "",
      labels: ingress.labels,
    };
  });
};

export const getHealthChecksFromEnvironment = (
  environment: Environment,
): IHealthCheck[] => {
  return (environment.health_checks ?? []) as unknown as IHealthCheck[];
};

// Lookup helper for built-in apps like jupyter and k8s-dashboard. Matches the
// legacy `app.name.startsWith(prefix) && app.name.endsWith("-" + workspaceId)`
// check — without a trailing dash on the prefix, since the user-supplied name
// can carry a dedup suffix (e.g. `jupyter0-{workspaceId}`).
export const findApplicationByHostnamePrefix = (
  environment: Environment,
  prefix: string,
): IApplication | undefined => {
  const applications = getIngressApplicationsFromEnvironment(environment);
  const appIDCandidates = [
    environment.workspaceId,
    environment.environment_id,
  ].filter((id): id is string => typeof id === "string" && id.length > 0);
  return applications.find(
    (app) =>
      app.name.startsWith(prefix) &&
      (appIDCandidates.length > 0
        ? appIDCandidates.some((id) => app.name.endsWith(`-${id}`))
        : true),
  );
};

// Transform API snake_case k8s config to frontend camelCase
const transformBuildConfig = (build: any): any => {
  if (!build?.vm?.k8s) return build;

  return {
    ...build,
    vm: {
      ...build.vm,
      jupyterInstall: build.vm.jupyter_install ?? build.vm.jupyterInstall,
      k8s: mapKeys(build.vm.k8s, (_value, key) => camelCase(key)),
    },
  };
};

export const transformWorkspaceToEnvironment = (
  workspace: IWorkspace,
): Environment | null => {
  if (!workspace.environment) {
    return null;
  }

  const environment = normalizeEnvironmentBytes(workspace.environment);

  return {
    ...environment,
    workspaceId: workspace.id,
    architecture: workspace.architecture ?? environment.architecture,
    verbBuildStatus: workspace.verbBuildStatus,
    health_checks:
      environment.health_checks ??
      (workspace.healthCheck as Environment["health_checks"]),
    tunnel: workspace.tunnel,
    // Transform k8s config from snake_case to camelCase
    build: environment.build
      ? transformBuildConfig(environment.build)
      : undefined,
  };
};

export function useNotebookDetailsFromEnvironmentFiles(
  files: File[],
  notebookList,
) {
  const notebookDetails = useMemo(() => {
    if (!files || !files.length || !notebookList) {
      return { name: "", path: "" };
    }
    const url = files[0].file_url;
    const currentName = getNotebookNameFromUrl(url || "");
    const found = notebookList.find((n) => {
      const extractedName = getNotebookNameFromUrl(n.file);
      return extractedName === currentName;
    });

    if (found) {
      return {
        name: currentName,
        path: found.name.replace(".html", ""),
        found: true,
      };
    }

    return { name: currentName, path: url, found: false };
  }, [files, notebookList]);

  return notebookDetails;
}

// Define the task status constants
export const TaskStatus = {
  WAITING: "waiting",
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  CANCELED: "canceled",
  FAILED: "failed",
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

// Define the task name constants
export const TaskName = {
  ENVIRONMENT_BUILD: "environment-build",
  ENVIRONMENT_PROVISION: "environment-provision",
  START_ENVIRONMENT_INSTANCE: "start-environment-instance",
} as const;

export type TaskName = (typeof TaskName)[keyof typeof TaskName];

// Helper functions to check environment states
export function isEnvironmentBuilding(tasks: Task[]): boolean {
  const currentTask = getLatestTask(TaskName.ENVIRONMENT_BUILD, tasks);
  if (!currentTask) {
    return false;
  }
  return currentTask.status === TaskStatus.RUNNING;
}

export function isEnvironmentWaiting(tasks: Task[]): boolean {
  const waitingBuildTasks = tasks.filter(
    (task) =>
      task.name === TaskName.ENVIRONMENT_BUILD &&
      task.status === TaskStatus.WAITING,
  );
  return waitingBuildTasks.length > 0;
}

export function didEnvironmentSucceed(tasks: Task[]): boolean {
  const currentTask = getLatestTask(TaskName.ENVIRONMENT_BUILD, tasks);
  if (!currentTask) {
    return false;
  }
  return currentTask.status === TaskStatus.SUCCEEDED;
}

export function didEnvironmentFail(tasks: Task[]): boolean {
  const currentTask = getLatestTask(TaskName.ENVIRONMENT_BUILD, tasks);
  if (!currentTask) {
    return false;
  }
  return currentTask.status === TaskStatus.FAILED;
}

// Helper function to get the latest task by name
export function getLatestTask(taskName: string, tasks: Task[]): Task | null {
  const filteredTasks = tasks.filter((task) => task.name === taskName);
  if (filteredTasks.length === 0) {
    return null;
  }

  // Sort tasks by creation time, most recent first
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const timeA = parseTimestamp(a.create_time);
    const timeB = parseTimestamp(b.create_time);
    // Handle cases where timestamps might be null
    if (!timeA && !timeB) return 0;
    if (!timeA) return 1; // b comes first if a is null
    if (!timeB) return -1; // a comes first if b is null

    // Safe to subtract now that we've handled null cases
    return timeB.getTime() - timeA.getTime();
  });

  return sortedTasks[0];
}

/**
 * Determines the verb build status based on the state of tasks
 * @param tasks Array of tasks
 * @returns The current verb build status
 */
export function getVerbBuildStatusFromTasks(tasks: Task[]): VerbBuildStatus {
  if (isEnvironmentBuilding(tasks)) {
    return VerbBuildStatus.Building;
  } else if (isEnvironmentWaiting(tasks)) {
    return VerbBuildStatus.Pending;
  } else if (didEnvironmentSucceed(tasks)) {
    return VerbBuildStatus.Completed;
  } else if (didEnvironmentFail(tasks)) {
    return VerbBuildStatus.CreateFailed;
  } else {
    return "" as VerbBuildStatus;
  }
}

// Constants for task names
const TaskNameStartEnvironmentInstance = "start-environment-instance";

// Constants for error messages
const insufficientResourcesMessage =
  "zone has insufficient resources to fulfill the request";
const insufficientQuotaMessage =
  "out of quota in the region fulfill the requested";

/**
 * Checks if the environment start task is currently running
 * @param tasks Array of tasks to check
 * @returns True if the environment is starting, false otherwise
 */
function isEnvironmentStarting(tasks: Task[]): boolean {
  const currentTask = getLatestTask(TaskNameStartEnvironmentInstance, tasks);
  if (currentTask === null) {
    return false;
  }
  return currentTask.status === TaskStatus.RUNNING;
}

/**
 * Checks if there are any waiting environment start tasks
 * @param tasks Array of tasks to check
 * @returns True if there are waiting start tasks, false otherwise
 */
function isEnvironmentStartWaiting(tasks: Task[]): boolean {
  const waitingStartTasks = tasks.filter(
    (task) =>
      task.name === TaskNameStartEnvironmentInstance &&
      task.status === TaskStatus.WAITING,
  );
  return waitingStartTasks.length > 0;
}

/**
 * Checks if the latest environment start task succeeded
 * @param tasks Array of tasks to check
 * @returns True if the environment start succeeded, false otherwise
 */
function didEnvironmentStartSucceed(tasks: Task[]): boolean {
  const currentTask = getLatestTask(TaskNameStartEnvironmentInstance, tasks);
  if (currentTask === null) {
    return false;
  }
  return currentTask.status === TaskStatus.SUCCEEDED;
}

/**
 * Checks if the latest environment start task failed and returns the failure message if any
 * @param tasks Array of tasks to check
 * @returns A tuple with [didFail, message]
 */
function didEnvironmentStartFail(tasks: Task[]): [boolean, string] {
  const currentTask = getLatestTask(TaskNameStartEnvironmentInstance, tasks);
  if (currentTask === null) {
    return [false, ""];
  }
  if (currentTask.status === TaskStatus.FAILED) {
    return [true, currentTask.message || ""];
  }
  return [false, ""];
}

/**
 * Formats a failure message for workspace start failures
 * @param message The raw error message
 * @returns A formatted workspace status message
 */
export function getWorkspaceStartFailureMessageFromTasks(
  tasks: Task[],
): string {
  const [didFail, message] = didEnvironmentStartFail(tasks);
  if (didFail) {
    if (message.includes(insufficientResourcesMessage)) {
      return insufficientResourcesMessage;
    } else if (message.includes(insufficientQuotaMessage)) {
      return insufficientQuotaMessage;
    }
    return message;
  } else {
    return "";
  }
}

/**
 * Determines both the start status and status message from the environment tasks
 * @param tasks Array of tasks to analyze
 * @returns A tuple with [startStatus, statusMessage]
 */
export function getWorkspaceStartStatusFromTasks(
  tasks: Task[],
): WorkspaceStartStatus {
  // Determine status based on task states
  if (isEnvironmentStarting(tasks)) {
    return WorkspaceStartStatus.Starting;
  } else if (isEnvironmentStartWaiting(tasks)) {
    return WorkspaceStartStatus.Starting;
  } else if (didEnvironmentStartSucceed(tasks)) {
    return WorkspaceStartStatus.Started;
  } else {
    const [didFail, message] = didEnvironmentStartFail(tasks);
    if (didFail) {
      return WorkspaceStartStatus.Failure;
    } else {
      return WorkspaceStartStatus.Unset;
    }
  }
}

/**
 * Checks if two EnvironmentBuild objects are equal by comparing all their properties
 */
export const isEnvironmentBuildSame = (
  a?: EnvironmentBuild,
  b?: EnvironmentBuild,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;

  // Compare verb builds
  if (!isEqual(a.verb, b.verb)) return false;

  // Compare vm builds
  if (!isEqual(a.vm, b.vm)) return false;

  // Compare container builds
  if (!isEqual(a.container, b.container)) return false;

  // Compare docker compose builds
  if (!isEqual(a.docker_compose, b.docker_compose)) return false;

  // Compare ingress gateway arrays
  if (!isEqual(a.ingress_gateway, b.ingress_gateway)) return false;

  // Compare lifecycle scripts arrays
  if (!isEqual(a.lifecycle_scripts, b.lifecycle_scripts)) return false;

  // Compare files arrays
  if (!isEqual(a.files, b.files)) return false;

  return true;
};

export const isLifeCycleScriptsSame = (
  a?: LifeCycleScript[],
  b?: LifeCycleScript[],
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;

  // Compare array lengths
  if (a.length !== b.length) return false;

  // Sort arrays by id to ensure consistent comparison
  const sortedA = [...a].sort((x, y) => x.id.localeCompare(y.id));
  const sortedB = [...b].sort((x, y) => x.id.localeCompare(y.id));

  // Compare each script in the arrays
  for (let i = 0; i < sortedA.length; i++) {
    if (!isLifeCycleScriptSame(sortedA[i], sortedB[i])) {
      return false;
    }
  }

  return true;
};

const isLifeCycleScriptSame = (
  a: LifeCycleScript,
  b: LifeCycleScript,
): boolean => {
  // Compare basic properties
  if (a.id !== b.id) return false;

  // Compare last_run properties
  if (!a.last_run && !b.last_run) return true;
  if (!a.last_run || !b.last_run) return false;

  const lastRunA = a.last_run;
  const lastRunB = b.last_run;

  return (
    lastRunA.run_id === lastRunB.run_id &&
    lastRunA.message === lastRunB.message &&
    lastRunA.status === lastRunB.status &&
    lastRunA.log_path === lastRunB.log_path &&
    isEqual(lastRunA.start_time, lastRunB.start_time) &&
    isEqual(lastRunA.end_time, lastRunB.end_time)
  );
};

/**
 * Helper function to check if two values are equal, handling undefined values
 */
const isEqual = <T,>(a?: T, b?: T): boolean => {
  if (a === b) return true;
  if (!a || !b) return true; // Both undefined/null are considered equal
  return JSON.stringify(a) === JSON.stringify(b);
};

export const doesPlatformSupportFirewallRules = (
  platformType: string,
): boolean => {
  return (
    platformType === "crusoe" ||
    platformType === "aws" ||
    platformType === "gcp"
  );
};

export const hasInfiniBand = (environment: Environment): boolean => {
  const networkDetails =
    environment.instance?.instance_type_info?.supported_gpus?.[0]
      ?.network_details;
  return networkDetails?.toLowerCase() === "infiniband";
};

export const getLocationFromEnvironment = (
  environment: Environment,
): string | null => {
  return environment.instance?.location || null;
};

export interface EnvironmentCluster {
  location: string;
  environments: Environment[];
}

export interface GroupedEnvironments {
  infinibandClusters: EnvironmentCluster[];
  ungroupedEnvironments: Environment[];
}

export const groupEnvironmentsByClusters = (
  environments: Environment[],
): GroupedEnvironments => {
  const clustersMap = new Map<string, Environment[]>();
  const ungrouped: Environment[] = [];

  environments.forEach((env) => {
    if (hasInfiniBand(env)) {
      const location = getLocationFromEnvironment(env);
      if (location) {
        const existing = clustersMap.get(location) || [];
        clustersMap.set(location, [...existing, env]);
      } else {
        ungrouped.push(env);
      }
    } else {
      ungrouped.push(env);
    }
  });

  const clusters = Array.from(clustersMap.entries()).map(
    ([location, envs]) => ({
      location,
      environments: envs,
    }),
  );

  return { infinibandClusters: clusters, ungroupedEnvironments: ungrouped };
};
